/* The size ceilings, as records you edit.
 *
 * They were nine constants and a union type built from their keys, which is why
 * there were exactly nine and why none could be renamed. A ceiling system is
 * one person's way of thinking about size; the next person's has a name this
 * file could never have shipped.
 *
 * The shape follows the broker editor, because it is the same migration: a
 * fixed set in the spec became stored records the user owns.
 *
 * ── the id is not the name ───────────────────────────────────────────────
 *
 * Renaming writes `name` and never `id`. Every `CapAssignment`, every seeded
 * book and every export names a ceiling by its id, so a rename that touched it
 * would silently un-cap every position pointing at it. This is where the theme
 * editor's shape would be wrong: a theme's key *is* its name, which costs it a
 * drop-and-rewrite, and a ceiling keyed by name would strand assignments in a
 * different store entirely.
 *
 * ── deleting one that is in use ──────────────────────────────────────────
 *
 * Blocked, but never a dead end. A plain block is a trap: an assignment for a
 * ticker sold last month is reachable from no screen, so the ceiling could
 * never be removed. A silent clear breaks the rule that nothing here changes a
 * record you did not ask it to. So the row names what points at it and offers
 * both ways out — move them to another ceiling, or clear them — each one write,
 * after which Remove works.
 *
 * The count comes from `caps`, not from the positions on screen: `deriveBook`
 * drops a ticker whose lots net to zero, nothing prunes the assignment when a
 * position is closed, and a rename carries it across. Counting what is visible
 * would under-count and let a ceiling be deleted out from under a holding that
 * comes back. */

import { useState } from 'react';
import { useStore } from '../state/store';
import { NumberField } from './NumberField';
import { bandIdFromName, bandNameTaken, sortBands } from '../lib/capBands';
import type { CapBandRecord } from '../types';

export function CeilingsSection({ say }: { say: (m: string) => void }) {
  const { state, putCapBand, removeCapBand, reassignCapBand } = useStore();
  const bands = state.data.capBands;
  const [adding, setAdding] = useState('');
  const [confirm, setConfirm] = useState<string | null>(null);
  const [moveTo, setMoveTo] = useState('');

  const usedBy = (id: string) => state.data.caps.filter((c) => c.capKind === id);

  const add = async () => {
    const name = adding.trim();
    if (name === '') return;
    if (bandNameTaken(bands, name)) {
      say('You already have a ceiling by that name.');
      return;
    }
    await putCapBand({ id: bandIdFromName(name), name, pct: 1 });
    setAdding('');
    say(`"${name}" added at 1%. Set the ceiling you want.`);
  };

  const remove = async (b: CapBandRecord) => {
    const ok = await removeCapBand(b.id);
    setConfirm(null);
    say(ok ? `"${b.name}" removed.` : 'Something is still set to it.');
  };

  return (
    <section>
      <div className="section-head">
        <h2>Size ceilings</h2>
        <span className="meta">{bands.length === 0 ? 'none' : `${bands.length}`}</span>
      </div>

      <div className="card card-pad">
        <div className="dim" style={{ fontSize: 11.5, lineHeight: 1.5, marginBottom: 12 }}>
          What each kind of position is allowed to grow to, as a share of the book. These are your
          names and your numbers — the nine here are a starting point. A position is measured
          against the one it is set to, and a position set to none is reported as undocumented.
        </div>

        {sortBands(bands).map((b) => {
          const used = usedBy(b.id);
          return (
            <div key={b.id} style={{ marginBottom: 14 }}>
              <div style={{ display: 'flex', gap: 8, alignItems: 'flex-end' }}>
                <div className="field" style={{ flex: 1, marginBottom: 0 }}>
                  <label htmlFor={`band-n-${b.id}`}>Name</label>
                  <input
                    id={`band-n-${b.id}`} className="input" value={b.name}
                    onChange={(e) => void putCapBand({ ...b, name: e.target.value })}
                  />
                </div>
                <div className="field" style={{ width: 104, marginBottom: 0 }}>
                  <label htmlFor={`band-p-${b.id}`}>Ceiling</label>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                    <NumberField
                      id={`band-p-${b.id}`} value={b.pct}
                      onChange={(n, text) => {
                        /* Blank is refused rather than stored: there is no
                           constant underneath this any more, and a ceiling of
                           nothing puts every position in the band past its trim
                           multiple at once. */
                        if (text.trim() === '' || !Number.isFinite(n) || n <= 0) return;
                        void putCapBand({ ...b, pct: n });
                      }}
                    />
                    <span className="dim" style={{ fontSize: 12 }}>%</span>
                  </div>
                </div>
              </div>

              <div className="field" style={{ marginTop: 8, marginBottom: 0 }}>
                <label htmlFor={`band-r-${b.id}`}>Why it exists</label>
                <input
                  id={`band-r-${b.id}`} className="input" value={b.reason ?? ''}
                  placeholder="optional"
                  onChange={(e) => {
                    const reason = e.target.value;
                    const next: CapBandRecord = { ...b };
                    if (reason.trim() === '') delete next.reason;
                    else next.reason = reason;
                    void putCapBand(next);
                  }}
                />
                <div className="hint">
                  Shown under &ldquo;Why this is flagged&rdquo; when a position goes past it. Left
                  empty, nothing is shown rather than a sentence nobody wrote.
                </div>
              </div>

              {confirm === b.id ? (
                <div className="note" data-tone={used.length > 0 ? 'crit' : 'warn'}
                     style={{ marginTop: 8 }}>
                  {used.length > 0 ? (
                    <>
                      {used.length === 1 ? '1 position is' : `${used.length} positions are`} set to
                      this ceiling: {used.map((c) => c.ticker).join(', ')}. Removing it would leave
                      them pointing at nothing. Move them or clear them first.
                      <div style={{ display: 'flex', gap: 8, marginTop: 10, flexWrap: 'wrap' }}>
                        <select className="select" style={{ flex: 1, minWidth: 130 }}
                                value={moveTo} onChange={(e) => setMoveTo(e.target.value)}>
                          <option value="">Move them to…</option>
                          {bands.filter((o) => o.id !== b.id).map((o) => (
                            <option key={o.id} value={o.id}>{o.name} · {o.pct}%</option>
                          ))}
                        </select>
                        <button className="btn btn-sm" disabled={moveTo === ''}
                                onClick={() => void reassignCapBand(b.id, moveTo)
                                  .then((n) => say(`${n} moved.`))}>
                          Move
                        </button>
                        <button className="btn btn-sm"
                                onClick={() => void reassignCapBand(b.id, null)
                                  .then((n) => say(`${n} left with no ceiling.`))}>
                          Clear the ceiling on these
                        </button>
                      </div>
                      <div style={{ marginTop: 8 }}>
                        <button className="btn btn-ghost btn-sm" onClick={() => setConfirm(null)}>
                          Keep it
                        </button>
                      </div>
                    </>
                  ) : (
                    <>
                      Nothing is set to this one, so removing it changes no position.
                      <div className="fab-row" style={{ marginTop: 9 }}>
                        <button className="btn btn-block" onClick={() => setConfirm(null)}>
                          Keep it
                        </button>
                        <button className="btn btn-danger btn-block" onClick={() => void remove(b)}>
                          Remove it
                        </button>
                      </div>
                    </>
                  )}
                </div>
              ) : (
                <div style={{ display: 'flex', gap: 10, alignItems: 'center', marginTop: 6 }}>
                  <span className="dim" style={{ fontSize: 11 }}>
                    {used.length === 0 ? 'nothing set to it'
                      : `${used.length} set to it · ${used.map((c) => c.ticker).join(', ')}`}
                  </span>
                  <button className="btn btn-ghost btn-sm" style={{ marginLeft: 'auto' }}
                          onClick={() => { setConfirm(b.id); setMoveTo(''); }}>
                    Remove
                  </button>
                </div>
              )}
              <div className="divider" />
            </div>
          );
        })}

        <div className="field" style={{ marginBottom: 0 }}>
          <label htmlFor="band-new">Add one of your own</label>
          <div style={{ display: 'flex', gap: 8 }}>
            <input id="band-new" className="input" style={{ flex: 1 }} value={adding}
                   placeholder="Gambling position"
                   onChange={(e) => setAdding(e.target.value)} />
            <button className="btn" disabled={adding.trim() === ''} onClick={() => void add()}>
              Add
            </button>
          </div>
          <div className="hint">
            It starts at 1% and you set the number. The name is yours; the alerts quote it back.
          </div>
        </div>

        {bands.length === 0 ? (
          <div className="note" style={{ marginTop: 12 }}>
            With no ceilings defined, nothing is measured against one and positions are not asked
            to have one. Add one and that starts again.
          </div>
        ) : null}
      </div>
    </section>
  );
}
